/**
 * Shared AFM™ rate + payment pricing simulator.
 *
 * Used by:
 *   - /market/portal/resources (investor portal)
 *   - /underwriting (warehouse / capital-markets reviewer portal)
 *
 * RatePricingSimulator — interactive: purchase price, down %, FICO →
 *   indicative rate, monthly P&I, tax, insurance, total.
 *
 * Indicative rate logic mirrors the loan-tape pricing tiers; production
 * loans price through Atlas server-side. This is for ranging only.
 *
 * PMI removed from the product — non-QM loans can't bind MI. The PMI
 * column / matrix / pill were dropped; total monthly is P&I + Tax +
 * Insurance.
 */
import React, { useMemo, useState } from 'react';

const PRIMARY = '#3a6ad9';

const fmt$ = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n, d = 2) => n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`;

// ── Rate simulator ───────────────────────────────────────────────────
function getIndicativeRate(ltv, fico) {
  let base;
  if (fico >= 760) base = 0.0925;
  else if (fico >= 720) base = 0.09375;
  else if (fico >= 680) base = 0.09500;
  else if (fico >= 640) base = 0.09625;
  else if (fico >= 620) base = 0.09750;
  else if (fico >= 600) base = 0.09875;
  else base = 0.10000;
  if (ltv > 0.90) base += 0.0025;
  else if (ltv > 0.85) base += 0.00125;
  return base;
}

const Stat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: '#111111' }}>{value}</div>
  </div>
);

export default function RatePricingSimulator() {
  const [purchasePrice, setPurchasePrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [fico, setFico] = useState(700);

  const calc = useMemo(() => {
    const price = Math.max(0, Number(purchasePrice) || 0);
    const dp = Math.max(0.10, Math.min(0.35, Number(downPct) / 100 || 0.20));
    const loanAmount = Math.round(price * (1 - dp));
    const ltv = price > 0 ? loanAmount / price : 0;
    const rate = getIndicativeRate(ltv, fico);
    const n = 360;
    const monthlyRate = rate / 12;
    const piti = loanAmount > 0 && monthlyRate > 0
      ? (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n))
      : 0;
    const monthlyTax = (price * 0.0175) / 12;
    const monthlyIns = (loanAmount * 0.006) / 12;
    const total = piti + monthlyTax + monthlyIns;
    return { price, dp, loanAmount, ltv, rate, piti, monthlyTax, monthlyIns, total };
  }, [purchasePrice, downPct, fico]);

  const ltvColor = calc.ltv <= 0.80 ? '#111111' : calc.ltv <= 0.90 ? '#111111' : '#ff082f';
  const ltvBg    = calc.ltv <= 0.80 ? '#f0fde4' : calc.ltv <= 0.90 ? '#fffde4' : '#fff5f6';

  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111111', boxSizing: 'border-box', outline: 'none' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Purchase Price</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }}>$</span>
            <input type="number" min={50000} step={5000} value={purchasePrice}
              onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)}
              style={{ ...inputStyle, paddingLeft: 22 }} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Down Payment %</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="range" min={10} max={35} step={1} value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              style={{ flex: 1, accentColor: PRIMARY }} />
            <input type="number" min={10} max={35} step={1} value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              style={{ ...inputStyle, width: 80 }} />
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>%</span>
          </div>
        </div>
        <div>
          <label style={labelStyle}>FICO Score</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="range" min={560} max={800} step={5} value={fico}
              onChange={(e) => setFico(Number(e.target.value))}
              style={{ flex: 1, accentColor: PRIMARY }} />
            <input type="number" min={560} max={800} step={5} value={fico}
              onChange={(e) => setFico(Number(e.target.value))}
              style={{ ...inputStyle, width: 90 }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Loan Amount</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111111' }}>{fmt$(calc.loanAmount)}</div>
        </div>
        <div style={{ background: ltvBg, border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>LTV</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: ltvColor }}>{(calc.ltv * 100).toFixed(1)}%</div>
        </div>
      </div>

      <div style={{ background: '#eff3fe', border: '1px solid #bfdbfe', borderRadius: 12, padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 14, marginBottom: 14 }}>
          <Stat label="Indicative Rate" value={fmtPct(calc.rate, 3)} />
          <Stat label="Monthly P&I" value={fmt$(Math.round(calc.piti))} />
          <Stat label="Monthly Tax (est.)" value={fmt$(Math.round(calc.monthlyTax))} />
          <Stat label="Monthly Ins. (est.)" value={fmt$(Math.round(calc.monthlyIns))} />
        </div>
        <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: 12, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Est. Monthly</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#111111' }}>{fmt$(Math.round(calc.total))}</span>
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
        Rates shown are indicative only based on current Doorly AFM™ program parameters. Actual rate and terms are subject to credit review and program eligibility. Not a commitment to lend. Tax estimate based on 1.75% effective rate (TX/FL avg). Insurance estimate based on 0.6% of loan amount.
      </p>
    </div>
  );
}
